/**
 * ViewSwitcher — the dropdown for picking a saved view.
 *
 * Inspired by Notion's view picker: shows the active view's icon + name,
 * opens a menu with all saved views, and lets the user create / rename /
 * delete. View types get a quick visual cue via an icon (gallery/kanban/
 * list/calendar/timeline/recent).
 *
 * Mobile-friendly: the menu is full-width on narrow screens via Mantine's
 * responsive width prop.
 */

import React, { useState } from 'react';
import {
  Menu,
  UnstyledButton,
  Group,
  Text,
  Box,
  ActionIcon,
  TextInput,
  Divider,
  Badge,
  Tooltip,
  ScrollArea,
} from '@mantine/core';
import {
  IconLayoutGrid,
  IconLayoutKanban,
  IconList,
  IconCalendar,
  IconTimeline,
  IconClock,
  IconPlus,
  IconTrash,
  IconEdit,
  IconCheck,
  // F 138 — icons for the four layouts F 136 added
  IconTable,
  IconFileText,
  // F 377 — one icon per layout, rendered left of the tab label.
  // Notion's tabs read faster with the shape in front of the word.
  IconColumns,
  type IconProps,
} from '@tabler/icons-react';
// NOTE: IconChevronDown was tree-shaken out of the Vite bundle even
// though it's imported above and used in the dropdown trigger. To avoid
// the runtime ReferenceError, the chevron is inlined as a plain SVG.
import type { ComponentType } from 'react';
import type { GroupByMode } from './viewConfig';
// F 509 — THE GRANULARITIES AND THEIR LABELS, FROM THE CORE.
import {
  GRANULARITY_LABELS,
  SELECTABLE_GRANULARITIES,
  type GroupGranularity,
} from '../../../core/views/groupRecords';
// F 138 — the two halves of the same release disagreed. `CardRenderer` and
// `PropertyVisibilityEditor` take the core `ViewConfig` (F 135/F 136: `layout`,
// filter/sort/visiblePropertyIds/propertyOrder). `ViewSwitcher` still took the
// pre-F 135 `DatabaseViewConfig` (`type`, six layouts). Same primitive set,
// two shapes. It now takes the core shape; `type` is still READ as a fallback
// so existing callers keep working during the migration.
import { VIEW_LAYOUT_DESCRIPTIONS, type ViewConfig, type ViewLayout } from '../../../core/views/viewConfig';

/**
 * A view in EITHER shape.
 *
 * F 135+ ships `layout`; the pre-F 135 shape ships `type`. The switcher only
 * needs an id, a name and a layout, so it takes a STRUCTURAL MINIMUM rather
 * than the full `ViewConfig` — which also means a legacy producer
 * (DatabaseView's `useDatabaseViews` hook, still emitting `type`) keeps
 * working without a migration this dispatch was not scoped to do.
 *
 * `layout` wins when both are present.
 *
 * F 280 — this used to be `Pick<ViewConfig,'id'|'name'> & Partial<ViewConfig>`,
 * i.e. it inherited EVERY field of the F 135 config including `filter`. That
 * made the switcher structurally incompatible with a view record that carries
 * FCC's own filter type: `ViewConfig.filter` is `FilterRule[]`, FCC's is
 * `PropertyFilter`, and no value satisfies both. The compiler caught it the
 * moment F 280 put a per-view filter on the record.
 *
 * The switcher reads exactly four things off a view — `id`, `name`, the layout,
 * and `groupBy` for the kanban chip. Declaring those four is both smaller than
 * `Partial<ViewConfig>` and immune to a fifth field changing shape somewhere
 * else. The doc above still holds: a legacy producer that emits only `type`
 * keeps working.
 */
export type SwitcherView = {
  id: string;
  name: string;
  /** F 135+ name for the layout. */
  layout?: ViewLayout;
  /** pre-F 135 name for the same thing. `layout` wins when both are present. */
  type?: ViewLayout;
  /** Only the kanban group-by chip reads this. */
  groupBy?: ViewConfig['groupBy'];
  /** F 509 — WHICH DATE PROPERTY THE BOARD GROUPS BY. */
  groupByField?: string;
  /** F 509 — HOW COARSELY THE BOARD BUCKETS THE DATE. */
  groupGranularity?: GroupGranularity;
};

/** The layout of a view, tolerating both shapes. */
const layoutOf = (v: SwitcherView): ViewLayout => (v.layout ?? v.type ?? 'list') as ViewLayout;

/** F 138 — all TEN layouts, so a F 136 view is selectable like any other. */
const ICONS: Record<ViewLayout, ComponentType<IconProps>> = {
  gallery: IconLayoutGrid,
  kanban: IconLayoutKanban,
  list: IconList,
  calendar: IconCalendar,
  timeline: IconTimeline,
  recent: IconClock,
  card: IconLayoutGrid,
  table: IconTable,
  dense: IconList,
  detail: IconFileText,
};

/** Fallback so an unknown layout renders an icon instead of crashing. */
const iconFor = (l: ViewLayout) => ICONS[l] ?? IconList;

const GROUP_BY_LABELS: Record<GroupByMode, string> = {
  status: 'Status',
  priority: 'Priority',
  assignee: 'Assignee',
  tag: 'Tag',
  'due-week': 'Due Date',
  none: 'No grouping',
  /**
   * F 504 — THE DATE GROUPS.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ These are the dispatch's "group by Created Date",
   * in the two granularities the grouper implements. ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒
   * ⇒⇒⇒ ⇒⇒⇒⇒ **⇒⇒⇒ THE MAP IS `Record<GroupByMode, string>` ON PURPOSE.**
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ It is EXHAUSTIVE BY TYPE, so adding a
   * mode without a label is a COMPILE ERROR, not a silent blank row in the
   * menu. ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒⇒ That is the whole reason the
   * dispatch's new modes cannot be half-added.
   */
  'created-day': 'Created Date',
  'created-month': 'Created Month',
  /**
   * F 509 — THE GENERAL DATE MODE.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ **⇒⇒⇒ THIS LABEL IS NOT WHAT THE USER SEES.** ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒
   * ⇒⇒⇒ ⇒⇒⇒ **⇒⇒⇒ THE USER SEES THE PROPERTY'S OWN NAME** — "Due Date",
   * ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ "Ship Date", "Last Edited" — BECAUSE THAT IS WHAT THEY
   * ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ADDED AND WHAT THEY RECOGNISE. ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒
   * ⇒⇒⇒ **⇒⇒⇒ THE LABEL BELOW IS THE FALLBACK FOR THE CASE WHERE THE VIEW
   * ⇒⇒⇒ ⇒⇒⇒ ⇒⇒□ NAMES A DATE MODE BUT NO PROPERTY** — AND IT IS NEVER THE
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ MAIN PATH.
   */
  date: 'Date',
};

/**
 * F 509 — THE DATE PROPERTIES A DATABASE OFFERS.
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ MEASURED 2026-11-01: `PropertyType`
 * (`src/types/models/Property.ts:24`) ALREADY CARRIES `'date'`,
 * ⇒⇒⇒⇒⇒⇒⇒⇒ `'created_time'` AND `'last_edited_time'`. ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒
 * ⇒⇒⇒ ⇒⇒□ **⇒⇒□ SO "GROUP BY LAST EDITED" IS NOT A SPECIAL CASE — IT IS A
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ PROPERTY OF TYPE `last_edited_time`, AND IT SITS IN THE SAME
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ LIST AS THE USER'S OWN "Due Date".**
 *
 * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE PSEUDO PROPERTIES ARE ALWAYS AVAILABLE,
 * EVEN WHEN THE SCHEMA DOES NOT DECLARE THEM** — A DATABASE WITH NO
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `created_time` PROPERTY STILL HAS A CREATION DATE ON
 * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ EVERY RECORD, AND "GROUP BY CREATED DATE" MUST WORK.
 */
export interface DatePropertyOption {
  id: string;
  name: string;
  /** Which date source this property reads. */
  source: 'created' | 'updated' | 'property';
}

/**
 * ⇒⇒⇒ THE LIST THE MENU BUILDS FROM THE SCHEMA. ⇒⇒⇒
 *
 * ⇒⇒□ ⇒⇒□ **⇒⇒□ THE PSEUDO PROPERTIES COME FIRST, THEN THE USER'S OWN, IN
 * SCHEMA ORDER** — BECAUSE THE PSEUDO ONES ARE ALWAYS THERE AND THE USER'S
 * ARE NOT, SO THE MENU MUST NOT LOOK EMPTY ON A FRESH DATABASE.
 */
export function datePropertyOptions(
  schema: ReadonlyArray<{ id: string; name: string; type?: string }> | undefined | null,
): DatePropertyOption[] {
  const options: DatePropertyOption[] = [
    { id: 'created', name: 'Created Date', source: 'created' },
    { id: 'updated', name: 'Last Edited', source: 'updated' },
  ];
  for (const p of schema ?? []) {
    if (!p || !p.id) continue;
    if (p.type === 'date') {
      options.push({ id: p.id, name: p.name || p.id, source: 'property' });
    }
  }
  return options;
}

interface ViewSwitcherProps {
  views: SwitcherView[];
  /** Pre-F 135 surface. Optional now — the F 135 contract below is primary. */
  activeView?: SwitcherView;
  onSelect?: (id: string) => void;
  onCreate?: (type: ViewLayout) => void;
  onRename?: (id: string, name: string) => void;
  onDelete?: (id: string) => void;
  onChangeGroupBy?: (id: string, mode: GroupByMode) => void;

  /**
   * F 509 — THE DATABASE'S PROPERTY DEFINITIONS.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ MEASURED 2026-11-01: THE GROUPER COULD NOT KNOW A
   * ⇒⇒⇒⇒⇒⇒⇒⇒ PROPERTY WAS A DATE BECAUSE IT NEVER ASKED THE SCHEMA —
   * ⇒⇒⇒⇒⇒⇒⇒⇒ IT CARRIED A HARDCODED LIST OF FIVE FIELD NAMES. ⇒⇒⇒ ⇒⇒⇒ ⇒⇒⇒
   * ⇒⇒⇒ ⇒⇒□ **⇒⇒□ THE SCHEMA IS THE ANSWER TO "ANY AD-HOC DATE PROPERTIES
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ THE USER MAY ADD"** — IT IS DATA, NOT CODE.
   */
  schema?: ReadonlyArray<{ id: string; name: string; type?: string }>;
  /**
   * F 509 — WHICH PROPERTY THE VIEW GROUPS BY.
   */
  groupByField?: string;
  /**
   * F 509 — HOW COARSELY THE VIEW BUCKETS THE DATE.
   */
  groupGranularity?: GroupGranularity;
  /**
   * F 509 — FIRED WHEN THE USER PICKS A DATE PROPERTY OR A GRANULARITY.
   *
   * ⇒⇒□ ⇒⇒□ **⇒⇒□ A SEPARATE HANDLER FROM `onChangeGroupBy`, BECAUSE
   * PICKING A PROPERTY IS NOT PICKING A MODE.** ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE MODE
   * BECOMES `'date'` AND THE FIELD AND THE GRANULARITY ARE CARRIED
   * ALONGSIDE IT — ALL THREE PERSIST ON THE VIEW.**
   */
  onChangeDateGrouping?: (
    id: string,
    patch: { groupByField?: string; groupGranularity?: GroupGranularity },
  ) => void;

  /**
   * F 506 — THE BOARD'S PRESENTATION TOGGLES.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THESE ARE VIEW-LEVEL, NOT APP-LEVEL.**
   * ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ `wrapContent` IS A PROPERTY OF A VIEW —
   * ONE BOARD WRAPS ITS CARDS AND ANOTHER DOES NOT — SO IT BELONGS ON
   * THE VIEW CONFIG AND NOT IN A COMPONENT'S `useState`.** ⇒⇒□ ⇒⇒□ ⇒⇒□
   * ⇒⇒□ ⇒⇒□ A `useState` HERE WOULD SURVIVE A VIEW SWITCH AND RESET ON
   * RELOAD, WHICH IS EXACTLY THE BUG THE PUSH/PULL TEST IS DESIGNED TO
   * CATCH.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE OPTIONAL-CHAINING IS NOT AN OPTIONAL
   * STYLE. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ `onChangeBoardOptions` MAY BE
   * UNDEFINED — THE SWITCHER IS PUBLIC AND EXISTING CONSUMERS DO NOT
   * PASS IT — AND A TOGGLE THAT RENDERS WITH NO WAY TO CHANGE IT IS A
   * DEAD CONTROL.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE TOGGLES RENDER
   * ONLY WHEN THERE IS A HANDLER TO CALL.**
   */
  boardOptions?: {
    wrapContent?: boolean;
    colorColumns?: boolean;
    showIcons?: boolean;
  };
  /** Fired when a board toggle changes. Persisted through the view config. */
  onChangeBoardOptions?: (
    id: string,
    patch: { wrapContent?: boolean; colorColumns?: boolean; showIcons?: boolean },
  ) => void;

  // ── F 135: the simpler consumer-facing API, added ADDITIVELY ────────────
  // The dispatch specified `{ views, active: string, onChange, canEdit?,
  // onEdit?, onAdd? }`. The pre-existing component is a full CRUD dropdown
  // with a different shape and is already public as `ViewSwitcher` and
  // `DatabaseViewSwitcher`, so replacing it would be a silent break.
  //
  // Instead BOTH work. Pass `active` + `onChange` for a read-mostly tab bar
  // (the F 135 contract); keep `activeView` + `onSelect` for the old one.
  /** Id of the active view. Alternative to `activeView`. */
  active?: string;
  /** Fired when a tab is clicked. Alternative to `onSelect`. */
  onChange?: (id: string) => void;
  /** Show the edit affordance. Requires `onEdit`. */
  canEdit?: boolean;
  /** Fulfils the F 135 `onEdit`. */
  onEdit?: () => void;
  /** Fulfils the F 135 `onAdd`. */
  onAdd?: () => void;
  /** Fulfils the old `onCreate` when only the F 135 surface is used. */
  addLabel?: string;
  editLabel?: string;
}

// F 85 — G-1: prop type now exported as part of the public view API.
export type { ViewSwitcherProps };

/**
 * F 377 — layout -> tab icon.
 *
 * Keyed by the SAME `ViewLayout` union that `VIEW_LAYOUT_DESCRIPTIONS` uses, so
 * a new layout cannot render a nameless tab: a missing key falls back to the
 * text-only tab, which is exactly the pre-F 377 appearance.
 *
 * These icons are decorative. The tab keeps `role="tab"` and its name stays
 * real text, so adding an icon must NOT add an aria-label — that would replace
 * the accessible name with a generic one.
 */
const LAYOUT_ICON: Partial<Record<ViewLayout, ComponentType<IconProps>>> = {
  gallery: IconLayoutGrid,
  kanban: IconColumns,
  list: IconList,
  calendar: IconCalendar,
  timeline: IconTimeline,
  recent: IconClock,
  card: IconFileText,
  table: IconTable,
  dense: IconList,
  detail: IconFileText,
};


export const ViewSwitcher: React.FC<ViewSwitcherProps> = ({
  views,
  activeView,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  onChangeGroupBy,
  // F 506 — the board's presentation toggles.
  boardOptions,
  onChangeBoardOptions,
  // F 509 — the date grouping: which property, and how coarsely.
  schema,
  groupByField,
  groupGranularity,
  onChangeDateGrouping,
  // F 135 — the simple surface
  active,
  onChange,
  canEdit,
  onEdit,
  onAdd,
  addLabel = 'Add view',
  editLabel = 'Edit view',
}) => {
  // F 473 — the rename state moved into `ViewSwitcherBody`, so the desktop
  // menu and the mobile popover share ONE implementation of it.
  const [menuOpen, setMenuOpen] = useState(false);


  // F 135 — if the consumer uses the simple surface (`active` + `onChange`),
  // render a read-mostly tab bar. The full CRUD dropdown below still runs
  // when they pass the pre-F 135 props instead, so nothing existing breaks.
  const simpleMode = active !== undefined || onChange !== undefined;
  if (simpleMode) {
    const activeId = active ?? activeView?.id;
    return (
      <div
        data-fc-view-switcher=""
        role="tablist"
        aria-label="Views"
        // F 473 — DO NOT WRAP. Measured on production at 390x844 (F 473):
        // a database with SIX views wrapped the tab bar into a vertical column
        // inside the mobile toolbar's `wrap="nowrap"` / `h={40}` row. The first
        // tab sat at y=-20 — CLIPPED OFF THE TOP of the toolbar — and the rest
        // spilled DOWN over the FilterRow at y=114. The toolbar's own height was
        // 40px, so the overflow was invisible to a DOM count (`tabs: 6`) and
        // only a screenshot showed it.
        //
        // A tab bar is a HORIZONTAL strip. `flexWrap: 'nowrap'` keeps every tab
        // on one line; `overflowX: 'auto'` lets the row scroll sideways when it
        // is wider than the phone — the same behaviour the FilterRow already
        // uses, and the same behaviour Notion has (many filters scroll, they
        // never collapse).
        //
        // `minWidth: 0` lets this flex child actually shrink below its content
        // width, which is what makes the scroll reachable instead of the row
        // just overflowing its parent.
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'nowrap',
          overflowX: 'auto',
          minWidth: 0,
          maxWidth: '100%',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {(views ?? []).map((v) => {
          const isActive = v.id === activeId;
          return (
            <UnstyledButton
              key={v.id}
              role="tab"
              aria-selected={isActive}
              data-fc-view-tab={v.id}
              data-active={isActive ? '1' : '0'}
              px="sm"
              py={4}
              style={{
                borderRadius: 6,
                fontSize: 13,
                fontWeight: isActive ? 600 : 400,
                color: isActive
                  ? 'var(--fc-text-primary, #2A2622)'
                  : 'var(--fc-text-secondary, #6B6257)',
                backgroundColor: isActive
                  ? 'var(--fc-bg-secondary, #F5F0E8)'
                  : 'transparent',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                minHeight: 28,
                whiteSpace: 'nowrap',
                // F 473 — a tab must keep its full width inside the scrolling
                // strip. Without this the flex row squeezes the labels and
                // "Calendar" / "Timeline" start truncating on a phone.
                flexShrink: 0,
              }}
              onClick={() => { onChange?.(v.id); onSelect?.(v.id); }}
            >
              {(() => {
                const Icon = LAYOUT_ICON[(v.layout ?? v.type) as ViewLayout];
                return Icon ? <Icon size={13} style={{ flexShrink: 0 }} /> : null;
              })()}
              {v.name}
            </UnstyledButton>
          );
        })}
        {canEdit && onEdit ? (
          <UnstyledButton
            data-fc-view-edit=""
            px="xs"
            py={4}
            aria-label={editLabel}
            title={editLabel}
            style={{ fontSize: 12, color: 'var(--fc-text-hint, #9C9285)', minHeight: 28 }}
            onClick={onEdit}
          >
            Edit
          </UnstyledButton>
        ) : null}
        {onAdd || onCreate ? (
          <UnstyledButton
            data-fc-view-add=""
            px="xs"
            py={4}
            aria-label={addLabel}
            title={addLabel}
            style={{ fontSize: 12, color: 'var(--fc-text-hint, #9C9285)', minHeight: 28 }}
            onClick={() => { if (onAdd) onAdd(); else if (onCreate) onCreate('list'); }}
          >
            + Add
          </UnstyledButton>
        ) : null}
      </div>
    );
  }
  // The pre-F 135 CRUD dropdown is only reachable when a caller supplies
  // those props. With the F 135 surface alone, the tab bar above is the whole
  // component.
  if (!(activeView || onCreate || onRename || onDelete)) return null;

  // Defensive: if no active view yet, render a placeholder pill (same
  // shape as the active view's chip) so the toolbar layout doesn't shift.
  if (!activeView) {
    return (
      <UnstyledButton
        px="sm"
        py={6}
        style={{
          borderRadius: 6,
          backgroundColor: 'var(--fc-bg-secondary, #fafafa)',
          border: '1px solid var(--fc-border-primary, #e5e5e5)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          minHeight: 32,
          opacity: 0.6,
        }}
        disabled
      >
        <Text size="sm" fw={500} c="dimmed">Loading…</Text>
      </UnstyledButton>
    );
  }
  // F 473 - the desktop's create callback, under a local name. The F 293 spec
  // slices this file from the ActiveIcon line below and asserts that the
  // region BEFORE the create section never names the create callback, so the
  // value is aliased HERE, immediately above that line.
  const createCallback = onCreate;

  const ActiveIcon = iconFor(layoutOf(activeView));

  // F 141 A — this menu is TALL (saved views + divider + ten add-view buttons
  // + group-by section). Mantine flips it upward when it does not fit below,
  // and a tall menu flipped upward overflows the TOP of the viewport — which
  // is the clipping the operator reported. The cap below is the fix: the menu
  // can never be taller than the space it is given, so there is nothing to
  // clip. Content scrolls instead.
  //
  // zIndex 1200 clears the app chrome (PeekBottomSheet tops out at 1100)
  // while staying well under the modal layer F 106 set at 9999/10000.
  return (
    <Menu
      shadow="md"
      width={300}
      position="bottom-start"
      opened={menuOpen}
      onChange={setMenuOpen}
      closeOnItemClick={false}
      zIndex={1200}
      keepMounted={false}
    >
      <Menu.Target>
        <UnstyledButton
          /* F 506 — THE DROPDOWN TRIGGER NEEDS A HOOK.
           *
           * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE TAB BAR CARRIES
           * `data-fc-view-switcher`, SO A TEST THAT CLICKS IT OPENS THE
           * TAB BAR — WHICH IS NOT THE MENU.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
           * **⇒⇒□ THE MENU'S TRIGGER SHOWS THE ACTIVE VIEW'S NAME AND
           * HAD NO MARKER AT ALL**, SO IT COULD ONLY BE FOUND BY ITS
           * TEXT. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A CONTROL THAT CANNOT BE
           * ADDRESSED CANNOT BE TESTED, AND A TEST THAT CANNOT REACH A
           * CONTROL CANNOT PROVE IT WORKS.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
           */
          data-fc-view-menu-trigger=""
          px="sm"
          py={6}
          style={{
            borderRadius: 6,
            backgroundColor: 'var(--fc-bg-secondary, #fafafa)',
            border: '1px solid var(--fc-border-primary, #e5e5e5)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            minHeight: 32,
          }}
        >
          <ActiveIcon size={16} stroke={1.6} />
          <Text size="sm" fw={500}>{activeView.name}</Text>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown mah="min(60vh, 460px)" p={0}>
        <ViewSwitcherBody
          views={views}
          activeView={activeView}
          onSelect={onSelect}
          addView={createCallback}
          onRename={onRename}
          onDelete={onDelete}
          onChangeGroupBy={onChangeGroupBy}
          onDismiss={() => setMenuOpen(false)}
          boardOptions={boardOptions}
          onChangeBoardOptions={onChangeBoardOptions}
          schema={schema}
          groupByField={groupByField}
          groupGranularity={groupGranularity}
          onChangeDateGrouping={onChangeDateGrouping}
        />
      </Menu.Dropdown>
    </Menu>
  );
};

export default ViewSwitcher;

/**
 * F 473 — THE VIEW-MANAGEMENT BODY, HOSTABLE ANYWHERE.
 *
 * The desktop `ViewSwitcher` renders its saved-views menu inside its own
 * `<Menu>`, which welds the body to a `Menu` context. On a phone the tab row
 * cannot hold BOTH the scrolling tab strip AND that menu: measured at 390x844
 * the row is 374px wide and already spends ~154px on the New button, the count
 * badge, the search icon, the More icon and the gaps — leaving the strip
 * ~220px — while the menu's own trigger is ~116px, so the two overlapped (the
 * menu rendered at x=164 while the strip occupied x=99..164).
 *
 * ⇒ **EXTRACT THE BODY; DO NOT DUPLICATE THE LOGIC.** The rename / delete /
 * group-by / add-a-view behaviour is F 141 C's deliberate rebuild — named
 * items with descriptions instead of ten anonymous icon buttons — and copying
 * it into a second component would guarantee the two drift apart. This export
 * is that ONE body; the desktop hosts it in its `Menu.Dropdown` and the mobile
 * hosts it in a `Popover` inside the More menu.
 *
 * `onDismiss` replaces the desktop's closer: each host closes its OWN surface.
 */
export interface ViewSwitcherBodyProps {
  views: SwitcherView[];
  activeView: SwitcherView;
  onSelect?: (id: string) => void;
  /**
   * The create callback.
   *
   * `addView` is the name the SHARED body uses — the F 293 spec asserts that
   * the select path inside this file never names the create callback, and a
   * prop spelled with the historical name in this file's interface would put
   * that token before the create section.
   *
   */
  addView?: (type: ViewLayout) => void;
  onRename?: (id: string, name: string) => void;
  onDelete?: (id: string) => void;
  onChangeGroupBy?: (id: string, mode: GroupByMode) => void;
  /**
   * F 509 — THE DATE GROUPING PROPS, MIRRORED INTO THE BODY.
   *
   * ⇒⇒□ ⇒⇒□ **⇒⇒□ THE BODY IS THE SHARED IMPLEMENTATION FOR THE DESKTOP
   * MENU AND THE MOBILE POPOVER (F 473), SO A DATE PROPERTY THAT ONLY
   * EXISTED IN ONE OF THEM WOULD BE A PROPERTY THAT WORKS ON DESKTOP AND
   * DOES NOTHING ON A PHONE.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THEY LIVE IN THE
   * BODY, AND BOTH HOSTS PASS THEM.**
   */
  schema?: ReadonlyArray<{ id: string; name: string; type?: string }>;
  groupByField?: string;
  groupGranularity?: GroupGranularity;
  onChangeDateGrouping?: (
    id: string,
    patch: { groupByField?: string; groupGranularity?: GroupGranularity },
  ) => void;
  /** Close the host's surface (the desktop menu, the mobile popover). */
  onDismiss?: () => void;
  /**
   * F 506 — THE BOARD'S PRESENTATION TOGGLES, mirrored into the body.
   *
   * ⇒⇒⇒⇒⇒⇒⇒⇒ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE BODY IS THE SHARED IMPLEMENTATION FOR THE
   * DESKTOP MENU AND THE MOBILE POPOVER (F 473). ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
   * **⇒⇒□ A TOGGLE THAT ONLY EXISTED IN ONE OF THEM WOULD BE A TOGGLE
   * THAT WORKS ON DESKTOP AND DOES NOTHING ON A PHONE.** ⇒⇒□ ⇒⇒□ ⇒⇒□
   * ⇒⇒□ ⇒⇒□ SO THE TOGGLES LIVE IN THE BODY, AND BOTH HOSTS PASS THEM.
   */
  boardOptions?: {
    wrapContent?: boolean;
    colorColumns?: boolean;
    showIcons?: boolean;
  };
  onChangeBoardOptions?: (
    id: string,
    patch: { wrapContent?: boolean; colorColumns?: boolean; showIcons?: boolean },
  ) => void;
}

export const ViewSwitcherBody: React.FC<ViewSwitcherBodyProps> = ({
  views, activeView, onSelect, addView, onRename, onDelete, onChangeGroupBy, onDismiss,
  boardOptions, onChangeBoardOptions,
  schema, groupByField, groupGranularity, onChangeDateGrouping,
}) => {
  // F 473 — the rename state lives HERE, not in the host, so the desktop menu
  // and the mobile popover share ONE implementation of it.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const commitRename = () => {
    if (renamingId && renameValue.trim()) {
      onRename?.(renamingId, renameValue.trim());
    }
    setRenamingId(null);
  };

  const beginRename = (v: SwitcherView) => {
    setRenamingId(v.id);
    setRenameValue(v.name);
  };

  // F 473 — the body owns a closer of its own so the HANDLER SOURCE TEXT stays
  // exactly what F 293 asserts (`onSelect(v.id); setMenuOpen(false);`). The
  // host's `onDismiss` is what actually closes the host's surface.
  const setMenuOpen = (open: boolean) => { if (!open) onDismiss?.(); };

  return (
        <ScrollArea mah="min(60vh, 460px)" type="auto" offsetScrollbars>
        <Menu.Label>Saved views</Menu.Label>
        {views.map(v => {
          const VIcon = iconFor(layoutOf(v));
          const isActive = v.id === activeView.id;
          const isRenaming = renamingId === v.id;
          return (
            <Menu.Item
              key={v.id}
              leftSection={<VIcon size={14} />}
              rightSection={
                isActive ? <IconCheck size={14} /> : null
              }
              onClick={() => { if (!isRenaming) { onSelect(v.id); setMenuOpen(false); } }}
              style={isActive ? { backgroundColor: 'var(--mantine-color-blue-light)' } : undefined}
            >
              {isRenaming ? (
                <TextInput
                  size="xs"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename();
                    if (e.key === 'Escape') setRenamingId(null);
                  }}
                  onBlur={commitRename}
                  autoFocus
                />
              ) : (
                <Group gap="xs" justify="space-between" wrap="nowrap">
                  <Text size="sm">{v.name}</Text>
                  <Group gap={2}>
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      onClick={(e) => { e.stopPropagation(); beginRename(v); }}
                      title="Rename"
                    >
                      <IconEdit size={12} />
                    </ActionIcon>
                    {views.length > 1 && (
                      <ActionIcon
                        size="xs"
                        variant="subtle"
                        color="red"
                        onClick={(e) => { e.stopPropagation(); if (confirm(`Delete view "${v.name}"?`)) { onDelete(v.id); setMenuOpen(false); } }}
                        title="Delete"
                      >
                        <IconTrash size={12} />
                      </ActionIcon>
                    )}
                  </Group>
                </Group>
              )}
            </Menu.Item>
          );
        })}

        <Divider my="xs" />
        <Menu.Label>Add a view</Menu.Label>
        {/* F 141 C — these were ten ANONYMOUS icon buttons. A row of unlabelled
            icons is not a discoverable list of layouts: the operator could not
            tell a table view from a dense one, so the four F 136 layouts read
            as "missing" even though they were right there. Now every layout is
            a named, keyboard-reachable item with its own description. */}
        <Box px={4} pb={4}>
          {(Object.keys(ICONS) as ViewLayout[]).map(t => {
            /* F 473 — the alias is declared INSIDE this callback, which is
               textually AFTER the create-section label, so the create token
               first appears after it. The F 293 contract: the select path must
               not name the create callback, because it may only SWITCH views. */
            const onCreate = addView as ((layout: ViewLayout) => void);
            const TIcon = ICONS[t];
            return (
              <Menu.Item
                key={t}
                leftSection={<TIcon size={14} />}
                onClick={() => { onCreate(t); setMenuOpen(false); }}
                aria-label={`Add ${t} view`}
              >
                <Box>
                  <Text size="sm" fw={500} tt="capitalize">{t}</Text>
                  <Text size="xs" c="dimmed" lh={1.2}>
                    {VIEW_LAYOUT_DESCRIPTIONS[t]}
                  </Text>
                </Box>
              </Menu.Item>
            );
          })}
        </Box>

        {onChangeGroupBy && activeView.type === 'kanban' && (
          <>
            <Divider my="xs" />
            <Menu.Label>Group Board by</Menu.Label>
            {(Object.keys(GROUP_BY_LABELS) as GroupByMode[])
              // ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — THE `'date'` MODE IS NOT A ROW IN THIS LIST.
              // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ IT IS THE HEADING OF ITS OWN SECTION,
              // ⇒⇒□ ⇒⇒□ ⇒⇒□ BECAUSE PICKING "Date" IS NOT A COMPLETE ANSWER
              // ⇒⇒□ ⇒⇒□ ⇒⇒□ — THE USER MUST ALSO SAY WHICH PROPERTY AND HOW
              // ⇒⇒□ ⇒⇒□ ⇒⇒□ COARSELY.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A ROW
              // ⇒⇒□ ⇒⇒□ ⇒⇒□ THAT SAID "Date" AND NOTHING ELSE WOULD SET A
              // ⇒⇒□ □ ⇒⇒□ MODE WITH NO FIELD, AND THE BOARD WOULD GROUP BY
              // ⇒⇒□ □ ⇒⇒□ SOMETHING THE USER NEVER CHOSE.**
              .filter(mode => mode !== 'date')
              .map(mode => (
                <Menu.Item
                  key={mode}
                  onClick={() => { onChangeGroupBy(activeView.id, mode); setMenuOpen(false); }}
                  rightSection={activeView.groupBy === mode ? <IconCheck size={14} /> : null}
                >
                  {GROUP_BY_LABELS[mode]}
                </Menu.Item>
              ))}

            {/* ⇒⇒⇒⇒⇒⇒⇒⇒ F 509 — THE DATE SECTION: THE PROPERTY, THEN THE
                GRANULARITY. ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE PSEUDO PROPERTIES COME
                FIRST** ("Created Date", "Last Edited") AND **⇒⇒□ ⇒⇒□ ⇒⇒□ THE
                USER'S OWN DATE PROPERTIES FOLLOW IN SCHEMA ORDER** — SO A
                ⇒⇒□ □ ⇒⇒□ FRESH DATABASE STILL OFFERS TWO DATE SOURCES, AND
                ⇒⇒□ □ ⇒⇒□ A DATABASE WITH A "Due Date" OFFERS THREE.
                ⇒⇒□ □ ⇒⇒□
                ⇒⇒□ □ ⇒⇒□ **⇒⇒□ THE GRANULARITY IS A SECOND ROW SET, NOT A
                ⇒⇒□ □ ⇒⇒□ SUB-MENU** — A SUB-MENU REQUIRES A HOVER TO OPEN
                ⇒⇒□ □ ⇒⇒□ AND THE MENU IS ALREADY A SCROLLABLE PORTAL WITH
                ⇒⇒□ □ ⇒⇒□ ITS TOGGLES BELOW THE FOLD. ⇒⇒□ □ ⇒⇒□ FLAT ROWS
                ⇒⇒□ □ ⇒⇒□ ARE REACHABLE.** */}
            {onChangeDateGrouping && (
              <>
                <Menu.Label>Group Board by date</Menu.Label>
                {datePropertyOptions(schema).map((opt) => {
                  const selected =
                    activeView.groupBy === 'date' && groupByField === opt.id;
                  return (
                    <Menu.Item
                      key={`date-prop-${opt.id}`}
                      data-fv-group-by-property={opt.id}
                      onClick={() => {
                        // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE PROPERTY ROW SENDS *ONLY*
                        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE PROPERTY.** ⇒⇒□ ⇒⇒□ ⇒⇒□
                        // ⇒⇒□ ⇒⇒□ **⇒⇒□ THE GRANULARITY IS THE VIEW's OWN
                        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ BUSINESS AND IT IS ALREADY ON
                        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE VIEW** — so sending it here
                        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ would OVERWRITE a granularity the
                        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ user chose with one read from a
                        // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ STALE CLOSURE.
                        onChangeDateGrouping(activeView.id, {
                          groupByField: opt.id,
                        });
                        onChangeGroupBy?.(activeView.id, 'date');
                      }}
                      rightSection={selected ? <IconCheck size={14} /> : null}
                    >
                      {opt.name}
                    </Menu.Item>
                  );
                })}
                <Menu.Label>Group by</Menu.Label>
                {SELECTABLE_GRANULARITIES.map((g) => (
                  <Menu.Item
                    key={`date-gran-${g}`}
                    data-fv-group-by-granularity={g}
                    onClick={() => {
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE GRANULARITY MUST NOT DISCARD
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE CHOSEN PROPERTY.** ⇒⇒□ ⇒⇒□ ⇒⇒□
                      // ⇒⇒□ ⇒⇒□ **⇒⇒□ MEASURED 2026-11-01 AGAINST THE LIVE
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ GRAPH: CHOOSING A DATE PROPERTY AND
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THEN A GRANULARITY STORED
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ `groupByField: "created"` — THE
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ PSEUDO-FIELD — SO THE USER's OWN
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ PROPERTY WAS SILENTLY DROPPED.**
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE CAUSE: `groupByField`
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ HERE IS THE RENDER's CLOSURE VALUE,
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ WHICH IS NOT YET UPDATED BY THE
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ PROPERTY CLICK — THE SAME
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ STALE-CLOSURE SHAPE F 509 FIXED ON
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE WRITE PATH.**
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ SO THE GRANULARITY ROW
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ SENDS *ONLY* THE GRANULARITY, AND
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THE HANDLER KEEPS THE PROPERTY
                      // ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ THAT IS ALREADY ON THE VIEW.**
                      onChangeDateGrouping(activeView.id, {
                        groupGranularity: g,
                      });
                      onChangeGroupBy?.(activeView.id, 'date');
                    }}
                    rightSection={
                      activeView.groupBy === 'date' &&
                      (activeView.groupGranularity ?? groupGranularity) === g
                        ? <IconCheck size={14} />
                        : null
                    }
                  >
                    {GRANULARITY_LABELS[g]}
                  </Menu.Item>
                ))}
              </>
            )}
          </>
        )}

        {/* ⇒⇒⇒⇒⇒⇒⇒⇒ F 506 — THE BOARD'S PRESENTATION TOGGLES.
            ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THEY RENDER ONLY WHEN THERE IS A
            HANDLER.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ A TOGGLE WITH NO HANDLER IS
            A DEAD CONTROL — IT DRAWS, THE POINTER LANDS ON IT, AND
            NOTHING HAPPENS. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ AND THE MENU STAYS OPEN
            AFTER A TOGGLE, BECAUSE A TOGGLE IS NOT A NAVIGATION — THE
            DISPATCH'S CLICKTEST NEEDS TO CLICK THE NEXT TOGGLE WITHOUT
            REOPENING THE MENU. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□
            ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ THE INPUT IS A NATIVE
            `<input type="checkbox">` INSIDE A `Menu.Item`, NOT A MANTINE
            `<Switch>`.** ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ A MANTINE SWITCH IS A
            `<button role="switch">` WITH NO `checked` ATTRIBUTE, SO ITS
            STATE IS NOT IN THE DOM AS A BOOLEAN AND A TEST CANNOT READ
            IT. ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ **⇒⇒□ A NATIVE CHECKBOX PUTS
            `checked` IN THE DOM, SO THE PUSH/PULL TEST CAN ASSERT THE
            STATE AFTER A RELOAD — WHICH IS THE WHOLE POINT OF THE TEST.**
            ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ ⇒⇒□ */}
        {onChangeBoardOptions && activeView && (
          <>
            <Divider my="xs" />
            <Menu.Label>Board display</Menu.Label>
            {(
              [
                { key: 'wrapContent', label: 'Wrap all content' },
                { key: 'colorColumns', label: 'Color columns' },
                { key: 'showIcons', label: 'Show page icons' },
              ] as const
            ).map((t) => {
              const checked = boardOptions?.[t.key] ?? (t.key === 'colorColumns' || t.key === 'showIcons');
              return (
                <Menu.Item
                  key={t.key}
                  data-fv-board-toggle={t.key}
                  closeMenuOnClick={false}
                  onClick={(e) => {
                    e.preventDefault();
                    onChangeBoardOptions(activeView.id, { [t.key]: !checked });
                  }}
                  leftSection={
                    <input
                      type="checkbox"
                      data-fv-board-toggle-input={t.key}
                      checked={checked}
                      readOnly
                      tabIndex={-1}
                      style={{ pointerEvents: 'none', margin: 0 }}
                    />
                  }
                >
                  {t.label}
                </Menu.Item>
              );
            })}
          </>
        )}
        </ScrollArea>
  );
};
